import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import type { SxProps, Theme } from '@mui/material';
import { Avatar } from '@mui/material';
import type { ReactNode } from 'react';

import { env } from '@/config/env';
import { resolveFileUrl } from '@/utils/formatters';

export interface CompanyLogoProps {
  /** The stored logo URL exactly as the API returned it — resolution against the gateway happens here. */
  logoUrl?: string | null;
  /** Company name, used for alt text. */
  name?: string | null;
  /** Rendered edge length in px. */
  size?: number;
  variant?: 'rounded' | 'circular' | 'square';
  sx?: SxProps<Theme>;
  /**
   * What shows when there is no logo. Defaults to the building icon; the job board passes the
   * company's initial instead. Kept as an override rather than standardised away — the fallback is a
   * per-surface design choice, and this component exists to centralise the LOADING policy, not to
   * quietly restyle six screens.
   */
  children?: ReactNode;
}

/**
 * A company logo — the platform's optimized image component for employer branding
 * (FrontendGuidelines.md §10.4, §15 Definition of Done).
 *
 * <p>It exists so the loading policy lives in ONE place. Logos appear in the employer directory, the
 * public job board, job detail, the company workspace and three admin screens; when each of those hand-
 * rolled its own `<Avatar src=...>`, "add `loading="lazy"`" was a change to nine files that a tenth
 * would silently skip. A directory page renders a grid of these, most of them below the fold, so
 * deferring the off-screen ones is the difference between paying for every employer on the page and
 * paying for the ones actually looked at.</p>
 *
 * <p>`decoding="async"` is paired with it deliberately: lazy loading defers the FETCH, but decoding a
 * large image still blocks the main thread when it arrives, which is what turns a scroll into a stutter.</p>
 *
 * <p>Sizing is bounded server-side too — uploads are resized before storage (ADR-0024), so this is not
 * shrinking a 2 MB original down to 44 px in the browser the way it used to.</p>
 */
export function CompanyLogo({
  logoUrl,
  name,
  size = 44,
  variant = 'rounded',
  sx,
  children,
}: CompanyLogoProps) {
  const src = logoUrl ? resolveFileUrl(logoUrl, env.gatewayUrl) : undefined;

  return (
    <Avatar
      src={src}
      alt={name ? `${name} logo` : undefined}
      variant={variant}
      slotProps={{ img: { loading: 'lazy', decoding: 'async' } }}
      sx={{ width: size, height: size, ...sx }}
    >
      {children ?? <BusinessOutlinedIcon fontSize="small" />}
    </Avatar>
  );
}
